import { MultiFactorAuthenticationError } from '@pba/client-auth';
import type { AxiosError } from 'axios';
import { useCallback } from 'react';
import { handleAxiosError } from 'ui';
import { useHandleMfaError } from '../../hooks';
import { useMessages } from './useMessages';
import { useShowLockedAccountModal } from './useShowLockedAccountModal';
import { useUseIsAxiosErrorWithResponseErrorField } from './useUseIsAxiosErrorWithResponseErrorField';

type P = {
  email: string;
  error: AxiosError;
  onExpiredPassword: (email: string) => Promise<void>;
  onNavigateToForgotPassword: (email?: string) => Promise<void>;
};

export const useUseHandleLoginError = ({
  onNavigateToCheckCode,
  onSuccessLogin,
}: {
  onNavigateToCheckCode: ({ tokenToGetCode, tokenToLogin }: { tokenToGetCode?: string; tokenToLogin?: string }) => void;
  onSuccessLogin: () => Promise<void>;
}) => {
  const { contextHolder: showUnblockAccountModalContext, showUnblockAccountModal } = useShowLockedAccountModal();
  const messages = useMessages();

  const isAxiosErrorWithResponseErrorField = useUseIsAxiosErrorWithResponseErrorField();
  const {
    contextHolder,
    handleError: handleMfaError,
    isMfaLoginLoading,
  } = useHandleMfaError({ onNavigateToCheckCode, onSuccessLogin });

  return {
    contextHolder: (
      <>
        {contextHolder} {showUnblockAccountModalContext}
      </>
    ),
    handleError: useCallback(
      async ({ email, error, onExpiredPassword, onNavigateToForgotPassword }: P) => {
        if (error instanceof MultiFactorAuthenticationError) {
          handleMfaError({ error });
          return;
        }

        if (isAxiosErrorWithResponseErrorField(error)) {
          if (error.response.data.error === 'password_expired') {
            await onExpiredPassword(email);
          } else if (error.response.data.error === 'user_locked') {
            showUnblockAccountModal({
              async onRequestUnblockAccount() {
                await onNavigateToForgotPassword(email);
              },
            });
          } else {
            const isValidMessageKey = (key: string): key is keyof typeof messages => key in messages;

            const errorValue = error.response.data.error;
            const responseErrorType: keyof typeof messages | undefined =
              typeof errorValue === 'string' && isValidMessageKey(errorValue) ? errorValue : undefined;

            const messageText = responseErrorType ? messages[responseErrorType] : undefined;
            handleAxiosError(error, messageText);
          }
        } else {
          handleAxiosError(error);
        }
      },
      [handleMfaError, isAxiosErrorWithResponseErrorField, messages, showUnblockAccountModal],
    ),
    isMfaLoginLoading,
  };
};
